import { type ChatUIMessage, resetMessagesAfterStop } from '@/entities/ai';
import { SendMessageInputLike, UseAiChatAbortHandlerProps } from './useAiChatAbortHandler.types';

/**
 * Abort handler hook for `useAiChat`.
 *
 * Cross-feature/cross-app deps (`useChatStore`, `useWidgetStore`,
 * `useUIStore`) are NOT imported here — they are injected via `props.deps`
 * (dependency injection pattern). The consuming page (`pages/StreamChatSession`) wires
 * the actual store setters and passes them down. This keeps `features/ai`
 * cross-feature-OUT and cross-app-OUT clean.
 */
export const useAiChatAbortHandler = ({ messageInFlight, deps }: UseAiChatAbortHandlerProps) => {
  const { setInput, setWidgetMessages, clearWidget, closeSidePanel } = deps;

  const abortToPreviousInput = (previousInput: SendMessageInputLike) => {
    setInput(() => ({
      value: previousInput.prompt,
      files:
        previousInput.files?.map((file) => ({
          ...file,
          // eslint-disable-next-line i18next/no-literal-string -- file upload status value
          status: 'uploaded',
          file: new File([], file.metadata.fileName),
        })) ?? [],
      options: previousInput.inputs,
    }));
  };

  const abortWidgetState = (removed: ChatUIMessage[]) => {
    setWidgetMessages((prev: Record<string, unknown[]>) => {
      const cloned: Record<string, unknown[]> = { ...prev };
      removed.forEach((message) => {
        cloned[message.id] = [];
      });
      return cloned;
    });
    closeSidePanel();
    clearWidget();
  };

  const receivedTokens = (message: ChatUIMessage) => {
    return message.role === 'assistant' && message.parts.some((part) => part.type === 'text');
  };

  const handleAbort = (
    setter: (messages: ChatUIMessage[] | ((prev: ChatUIMessage[]) => ChatUIMessage[])) => void,
  ) => {
    setter((prev) => {
      const previousInput = messageInFlight.current ? { ...messageInFlight.current } : null;
      const { messages, removed } = resetMessagesAfterStop(prev);
      if (removed.length > 0) {
        abortWidgetState(removed);

        const lastMessage = removed[removed.length - 1];
        if (!receivedTokens(lastMessage) && previousInput) {
          abortToPreviousInput(previousInput);
        }
      }

      return messages;
    });
  };

  return handleAbort;
};
